const CONSENT_KEY_PREFIX = "meetingPermissionConsent:";
/** Görüşme yeni sekmede açıldığı için localStorage kullanılır (sessionStorage sekmelere taşınmaz). */
const CONSENT_MAX_AGE_MS = 6 * 60 * 60 * 1000;

export function getMeetingPermissionConsentKey(appointmentId) {
  return `${CONSENT_KEY_PREFIX}${appointmentId}`;
}

function readConsentRaw(appointmentId) {
  const key = getMeetingPermissionConsentKey(appointmentId);
  try {
    const fromLocal = window.localStorage.getItem(key);
    if (fromLocal) return fromLocal;
  } catch {
    /* ignore */
  }
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function readMeetingPermissionConsent(appointmentId) {
  if (!appointmentId || typeof window === "undefined") return {};
  try {
    const raw = readConsentRaw(appointmentId);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    if (parsed.savedAt && Date.now() - parsed.savedAt > CONSENT_MAX_AGE_MS) {
      const key = getMeetingPermissionConsentKey(appointmentId);
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        /* ignore */
      }
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

export function writeMeetingPermissionConsent(appointmentId, patch) {
  if (!appointmentId || typeof window === "undefined") return;
  const prev = readMeetingPermissionConsent(appointmentId);
  const payload = JSON.stringify({
    ...prev,
    ...patch,
    savedAt: Date.now(),
  });
  const key = getMeetingPermissionConsentKey(appointmentId);
  try {
    window.localStorage.setItem(key, payload);
  } catch {
    /* ignore */
  }
  try {
    window.sessionStorage.setItem(key, payload);
  } catch {
    /* ignore */
  }
}

/**
 * Tarayıcıdan tek cihaz için getUserMedia izni ister; başarıda track'leri kapatır.
 * Jitsi iframe farklı origin'de olduğu için görüşme öncesi/sırasında çağrılmamalı —
 * parent GUM Windows'ta mikrofonu kilitleyip sessiz track üretebilir.
 * @param {'audio'|'video'} kind
 * @returns {Promise<boolean>}
 */
export async function requestBrowserMediaPermission(kind) {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return false;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia(
      kind === "audio" ? { audio: true } : { video: true }
    );
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch {
    return false;
  }
}

/**
 * Permissions API ile mevcut durum (desteklenmeyen tarayıcılarda null).
 * @param {'microphone'|'camera'} name
 * @returns {Promise<'granted'|'denied'|'prompt'|null>}
 */
export async function queryBrowserMediaPermissionState(name) {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return null;
  }
  try {
    const status = await navigator.permissions.query({ name });
    return status?.state ?? null;
  } catch {
    return null;
  }
}

/**
 * Görüşme sırasında cihaz açılırken: izin yoksa tarayıcıdan iste, consent'i güncelle.
 * @returns {Promise<boolean>} izin alındıysa true
 */
export async function ensureMeetingMediaPermission(appointmentId, kind) {
  const consent = readMeetingPermissionConsent(appointmentId);
  const consentKey =
    kind === "audio" ? "microphonePermissionConsent" : "cameraPermissionConsent";
  const permissionName = kind === "audio" ? "microphone" : "camera";

  const state = await queryBrowserMediaPermissionState(permissionName);
  if (consent[consentKey] && state === "granted") {
    return true;
  }
  if (consent[consentKey] && state === null) {
    const ok = await requestBrowserMediaPermission(kind);
    return ok;
  }

  const granted = await requestBrowserMediaPermission(kind);
  if (granted) {
    writeMeetingPermissionConsent(appointmentId, {
      [consentKey]: true,
      source: "meetingInCallReRequest",
    });
  }
  return granted;
}
